import { createStitches } from '@stitches/react';
import { Tokens } from '@mirohq-internal/design-tokens';
import React from 'react';

const themeMap = {
  background: "colors",
  backgroundColor: "colors",
  backgroundImage: "colors",
  blockSize: "sizes",
  border: "colors",
  borderBlock: "colors",
  borderBlockEnd: "colors",
  borderBlockStart: "colors",
  borderBottom: "colors",
  borderBottomColor: "colors",
  borderBottomLeftRadius: "radii",
  borderBottomRightRadius: "radii",
  borderBottomStyle: "border-styles",
  borderBottomWidth: "border-widths",
  borderColor: "colors",
  borderImage: "colors",
  borderInline: "colors",
  borderInlineEnd: "colors",
  borderInlineStart: "colors",
  borderLeft: "colors",
  borderLeftColor: "colors",
  borderLeftStyle: "border-styles",
  borderLeftWidth: "border-widths",
  borderRadius: "radii",
  borderRight: "colors",
  borderRightColor: "colors",
  borderRightStyle: "border-styles",
  borderRightWidth: "border-widths",
  borderStyle: "border-styles",
  borderTop: "colors",
  borderTopColor: "colors",
  borderTopLeftRadius: "radii",
  borderTopRightRadius: "radii",
  borderTopStyle: "border-styles",
  borderTopWidth: "border-widths",
  borderWidth: "border-widths",
  bottom: "space",
  boxShadow: "shadows",
  caretColor: "colors",
  color: "colors",
  columnGap: "space-gap",
  columnRuleColor: "colors",
  fill: "colors",
  flexBasis: "sizes",
  fontFamily: "fonts",
  fontSize: "font-sizes",
  fontWeight: "font-weights",
  gap: "space-gap",
  gridColumnGap: "space-gap",
  gridGap: "space-gap",
  gridRowGap: "space-gap",
  gridTemplateColumns: "sizes",
  gridTemplateRows: "sizes",
  height: "sizes",
  inlineSize: "sizes",
  inset: "space-inset",
  insetBlock: "space-inset",
  insetBlockEnd: "space-inset",
  insetBlockStart: "space-inset",
  insetInline: "space-inset",
  insetInlineEnd: "space-inset",
  insetInlineStart: "space-inset",
  left: "space",
  letterSpacing: "letter-spacings",
  lineHeight: "line-heights",
  margin: "space-offset",
  marginBlock: "space-offset",
  marginBlockEnd: "space-offset",
  marginBlockStart: "space-offset",
  marginBottom: "space-offset",
  marginInline: "space-offset",
  marginInlineEnd: "space-offset",
  marginInlineStart: "space-offset",
  marginLeft: "space-offset",
  marginRight: "space-offset",
  marginTop: "space-offset",
  maxBlockSize: "sizes",
  maxHeight: "sizes",
  maxInlineSize: "sizes",
  maxWidth: "sizes",
  minBlockSize: "sizes",
  minHeight: "sizes",
  minInlineSize: "sizes",
  minWidth: "sizes",
  outline: "colors",
  outlineColor: "colors",
  padding: "space-inset",
  paddingBlock: "space-inset",
  paddingBlockEnd: "space-inset",
  paddingBlockStart: "space-inset",
  paddingBottom: "space-inset",
  paddingInline: "space-inset",
  paddingInlineEnd: "space-inset",
  paddingInlineStart: "space-inset",
  paddingLeft: "space-inset",
  paddingRight: "space-inset",
  paddingTop: "space-inset",
  right: "space",
  rowGap: "space-gap",
  scrollMargin: "space-offset",
  scrollMarginBlock: "space-offset",
  scrollMarginBlockEnd: "space-offset",
  scrollMarginBlockStart: "space-offset",
  scrollMarginBottom: "space-offset",
  scrollMarginInline: "space-offset",
  scrollMarginInlineEnd: "space-offset",
  scrollMarginInlineStart: "space-offset",
  scrollMarginLeft: "space-offset",
  scrollMarginRight: "space-offset",
  scrollMarginTop: "space-offset",
  scrollPadding: "space-inset",
  scrollPaddingBlock: "space-inset",
  scrollPaddingBlockEnd: "space-inset",
  scrollPaddingBlockStart: "space-inset",
  scrollPaddingBottom: "space-inset",
  scrollPaddingInline: "space-inset",
  scrollPaddingInlineEnd: "space-inset",
  scrollPaddingInlineStart: "space-inset",
  scrollPaddingLeft: "space-inset",
  scrollPaddingRight: "space-inset",
  scrollPaddingTop: "space-inset",
  stroke: "colors",
  textDecorationColor: "colors",
  textShadow: "shadows",
  top: "space",
  transition: "transitions",
  width: "sizes",
  zIndex: "z-indices"
};

const theme$1 = {
  "border-widths": Tokens.borderWidths,
  colors: Tokens.colors,
  "font-sizes": Tokens.fontSizes,
  radii: Tokens.radii,
  shadows: Tokens.shadows,
  sizes: Tokens.sizes,
  space: Tokens.space,
  "space-gap": Tokens.spaceGap,
  "space-inset": Tokens.spaceInset,
  "space-offset": Tokens.spaceOffset,
  "z-indices": Tokens.zIndices
};

const utils = {
  paddingX: (value) => ({
    paddingLeft: value,
    paddingRight: value
  }),
  paddingY: (value) => ({
    paddingTop: value,
    paddingBottom: value
  }),
  marginX: (value) => ({
    marginLeft: value,
    marginRight: value
  }),
  marginY: (value) => ({
    marginTop: value,
    marginBottom: value
  })
};

const media = {};

const stitches = createStitches({
  theme: theme$1,
  media,
  utils,
  themeMap
});
const {
  config,
  createTheme,
  css,
  getCssText,
  globalCss,
  keyframes,
  prefix,
  styled: styled$1,
  theme
} = stitches;
const fontFace = (...fonts) => globalCss({
  "@font-face": fonts
})();

function setMedia(media) {
  config.media = {
    ...config.media,
    ...media
  };
}
function setCssUtils(utils) {
  config.utils = {
    ...config.utils,
    ...utils
  };
}

const AS_ERROR = "Polymorphism via `as` prop is not available in styled components.";
const STYLING_ATTRS_ERROR = "The `className` and `style` attributes are not avaialable in styled components.";
const styleWithCssVars = (style, themeMap) => Object.entries(style).reduce(
  (acc, [prop, value]) => {
    var _a;
    const mapKeyForProp = themeMap[prop];
    const mapping = theme[mapKeyForProp];
    const parsedValue = typeof value === "string" && /^\$.+$/.test(value) ? (_a = mapping[value.replace("$", "")]) == null ? void 0 : _a.computedValue : value;
    return {
      ...acc,
      [prop]: parsedValue
    };
  },
  {}
);
const styled = (element, composers) => {
  const StyledComponent = styled$1(element, composers != null ? composers : {});
  const Component = React.forwardRef(
    (props, forwardRef) => {
      const {
        as,
        className,
        style,
        UNSAFE_style,
        ...restProps
      } = props;
      const onlyStyledClasses = typeof className === "string" ? className == null ? void 0 : className.split(" ").filter((x) => x.match(`${prefix}c-.+`)).join(" ") : "";
      const parsedStyle = UNSAFE_style !== void 0 ? styleWithCssVars(UNSAFE_style, config.themeMap) : onlyStyledClasses !== "" ? style : void 0;
      if (className !== void 0 && onlyStyledClasses !== className || typeof style === "object" && style !== null && Object.keys(style).length > 0 && onlyStyledClasses === "") {
        console.error(STYLING_ATTRS_ERROR);
      }
      if (as !== void 0) {
        console.error(AS_ERROR);
      }
      return /* @__PURE__ */ React.createElement(StyledComponent, {
        ...restProps,
        className: onlyStyledClasses,
        style: parsedStyle,
        ref: forwardRef
      });
    }
  );
  Component.displayName = "Styled.ForwardRef";
  Component.toString = StyledComponent.toString;
  Component.className = StyledComponent.className;
  Component.selector = StyledComponent.selector;
  return Component;
};

export { AS_ERROR, prefix as CLASS_PREFIX, STYLING_ATTRS_ERROR, createTheme, css, fontFace, getCssText, globalCss, keyframes, setCssUtils, setMedia, styled, theme$1 as theme, themeMap };
//# sourceMappingURL=module.js.map
